import {
  WidgetEventType,
  initAeroSyncWidget,
  AerosyncEnvironment,
  WidgetEventSuccessType,
} from "aerosync-web-sdk";
import React, { useEffect, useRef } from "react";

import {
  AnalyticsEvents,
  capturePosthogEvent,
} from "../../common/utils/analytics";
import { Env } from "../../common/constants";
import { RootState } from "../../redux/store";
import { setLoading } from "../../redux/global.slice";
import { useAppDispatch } from "../../hooks/useAppDispatch";
import { useAppSelector } from "../../hooks/useAppSelector";
import { linkAeropayAccount } from "../../pages/subscription/services/action";
import { getAccountListing } from "../../pages/accounts-management/services/action";

interface AeroSyncProps {
  token: string;
  handleClose: () => void;
}

const AeroSyncWidget: React.FC<AeroSyncProps> = ({ token, handleClose }) => {
  const dispatch = useAppDispatch();
  const triggeredRef = useRef<boolean>(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const uid = useAppSelector((state: RootState) => state.verifyEmail.userId);

  const AeropayEnvironment =
    Env.MODE === "production"
      ? AerosyncEnvironment.Production
      : AerosyncEnvironment.Sandbox;

  useEffect(() => {
    if (!token) return;

    // Patch removeChild on the parent node to prevent React crashes if the SDK manipulates the DOM directly
    const parent = containerRef.current?.parentNode;
    let originalRemoveChild: any = null;
    if (parent) {
      originalRemoveChild = parent.removeChild.bind(parent);
      parent.removeChild = function (child) {
        if (child.parentNode === parent) {
          return originalRemoveChild(child);
        }
        return child;
      };
    }

    const widgetControls = initAeroSyncWidget({
      token,
      theme: "light",
      iframeTitle: "Connect",
      elementId: "aerosync-widget",
      environment: AeropayEnvironment,
      consumerId: Env.AEROPAY_CONSUMER_ID,

      onLoad() {
        console.log("Payment Widget loaded");
      },

      onEvent(event: WidgetEventType) {
        console.log("Payment event:", event);
      },

      onSuccess(event: WidgetEventSuccessType) {
        if (triggeredRef.current) return;
        triggeredRef.current = true;

        const dataToSend = {
          userId: event?.user_id,
          userPassword: event?.user_password,
        };

        dispatch(
          linkAeropayAccount(dataToSend, () => {
            dispatch(setLoading(true));
            capturePosthogEvent(AnalyticsEvents.BANK_DETAILS_UPDATED, {
              action: "add_bank_account",
              user_id: uid,
              aeropay_identifier: {
                userBankAccountId: event?.FILoginAcctId,
              },
            });
            dispatch(getAccountListing());
          }),
        );
      },

      onClose() {
        setTimeout(() => {
          handleClose();
        }, 0);
        console.log("Payment widget closed by user");
      },

      onError(error: string) {
        setTimeout(() => {
          handleClose();
        }, 0);
        console.error("Payment error:", error);
      },
    });

    widgetControls.launch();

    // Cleanup if needed
    return () => {
      // Restore original removeChild
      if (parent && originalRemoveChild) {
        parent.removeChild = originalRemoveChild;
      }
      const container = document.getElementById("aerosync-widget");
      if (container) container.innerHTML = "";
    };
    // eslint-disable-next-line
  }, [token]);

  return <div id="aerosync-widget" ref={containerRef} />;
};

export default AeroSyncWidget;
